import { useAuth } from '../../context/AuthContext';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
/**
 * Página de Despesas
 * Gerencia despesas e custos da clínica
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
  Grid,
  Autocomplete,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  DeleteOutline as DeleteOutlineIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { listarDespesas, criarDespesa, atualizarDespesa, deletarDespesa, uploadComprovante, removerComprovante } from '../../services/despesasService';
import { listarDespesasRecorrentes, criarDespesaRecorrente, cancelarDespesaRecorrente } from '../../services/despesaRecorrenteService';
import planoContasService from '../../services/planoContasService';

/**
 * Página de Despesas
 */

const Despesas = () => {
  const { user } = useAuth();
  const { isDarkMode } = useAppTheme();
  const planoAtual = user?.plano || user?.clinica?.plano || 'FREE';
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [itemEdicao, setItemEdicao] = useState(null);
  const [openExcluirModal, setOpenExcluirModal] = useState(false);
  const [itemExcluir, setItemExcluir] = useState(null);
  const [categoriasDespesas, setCategoriasDespesas] = useState([]);
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    categoria: '',
    tipo: 'variavel',
    observacoes: '',
    repetirMensalmente: false,
    duracaoMeses: '',
  });
  const [pesquisa, setPesquisa] = useState('');
  const [mesFiltro, setMesFiltro] = useState(new Date().getMonth() + 1);
  const [anoFiltro, setAnoFiltro] = useState(new Date().getFullYear());
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [recorrencias, setRecorrencias] = useState([]);
  const [openRecorrenciasModal, setOpenRecorrenciasModal] = useState(false);
  const [cancelandoId, setCancelandoId] = useState(null);
  const [comprovanteAtual, setComprovanteAtual] = useState(null);
  const [arquivoComprovante, setArquivoComprovante] = useState(null);

  useEffect(() => {
    carregarDados();
    carregarCategorias();
    carregarRecorrencias();
  }, []);

  const carregarCategorias = async () => {
    try {
      const categorias = await planoContasService.listar();
      setCategoriasDespesas(categorias || []);
    } catch (err) {
      setCategoriasDespesas([]);
    }
  };

  const carregarRecorrencias = async () => {
    try {
      const resp = await listarDespesasRecorrentes();
      setRecorrencias(resp.data || []);
    } catch (err) {
      setRecorrencias([]);
    }
  };

  const handleCancelarRecorrencia = async (id) => {
    const confirmado = window.confirm(
      'Cancelar essa despesa fixa? As parcelas futuras (ainda não vencidas) serão removidas. As que já venceram continuam normalmente.'
    );
    if (!confirmado) return;
    setCancelandoId(id);
    try {
      await cancelarDespesaRecorrente(id);
      await carregarRecorrencias();
      await carregarDados();
    } catch (err) {
      setError('Erro ao cancelar despesa fixa recorrente');
    } finally {
      setCancelandoId(null);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const response = await listarDespesas();
      setDespesas(response.data || []);
    } catch (err) {
      setError('Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalNovo = () => {
    setModoEdicao(false);
    setItemEdicao(null);
    setComprovanteAtual(null);
    setArquivoComprovante(null);
    setFormData({
      descricao: '',
      valor: '',
      data: new Date().toISOString().split('T')[0],
      categoria: '',
      tipo: 'variavel',
      observacoes: '',
      repetirMensalmente: false,
      duracaoMeses: '',
    });
    setOpenModal(true);
  };

  const abrirModalEdicao = (item) => {
    setModoEdicao(true);
    setItemEdicao(item);
    setArquivoComprovante(null);
    setComprovanteAtual(item.comprovanteUrl ? { url: item.comprovanteUrl, nome: item.comprovanteNome } : null);
    setFormData({
      descricao: item.descricao || '',
      valor: item.valor || '',
      data: item.data || '',
      categoria: item.categoria || '',
      tipo: item.tipo || 'variavel',
      observacoes: item.observacoes || '',
      repetirMensalmente: false,
      duracaoMeses: '',
    });
    setOpenModal(true);
  };

  const fecharModal = () => {
    setOpenModal(false);
    setComprovanteAtual(null);
    setArquivoComprovante(null);
    setError('');
  };

  const handleRemoverComprovante = async () => {
    // Se ainda não salvou (arquivo local), apenas limpa
    if (arquivoComprovante && !comprovanteAtual) {
      setArquivoComprovante(null);
      return;
    }
    if (!itemEdicao) return;
    try {
      await removerComprovante(itemEdicao.id);
      setComprovanteAtual(null);
      setArquivoComprovante(null);
      setDespesas(prev => prev.map(d => d.id === itemEdicao.id
        ? { ...d, comprovanteUrl: null, comprovanteNome: null, comprovanteTamanho: null }
        : d
      ));
    } catch (err) {
      setError('Erro ao remover comprovante');
    }
  };

  const formatarData = (data) => {
    return new Date(data + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return parseFloat(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Formata valor para moeda brasileira enquanto digita
  const formatarValorInput = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    if (!numeros) return '';
    const valorEmCentavos = parseInt(numeros, 10);
    const valorFormatado = (valorEmCentavos / 100).toLocaleString('pt-BR', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return valorFormatado;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      setLoading(true);
      const categoriaObj = categoriasDespesas.find(cat => (cat.id || cat) === formData.categoria);
      // Converte valor do formato brasileiro para número
      const valorNumerico = parseFloat(
        formData.valor
          .replace(/\./g, '') // remove pontos de milhar
          .replace(/,/g, '.') // troca vírgula por ponto decimal
      );
      const dados = {
        descricao: formData.descricao,
        valor: valorNumerico,
        data: formData.data,
        categoria: categoriaObj?.nome || formData.categoria,
        tipo: formData.tipo,
        observacoes: formData.observacoes
      };
      let despesaId;
      if (modoEdicao) {
        await atualizarDespesa(itemEdicao.id, dados);
        despesaId = itemEdicao.id;
      } else if (formData.tipo === 'fixa' && formData.repetirMensalmente) {
        // Despesa fixa recorrente: cria o "molde" e o backend já gera a
        // primeira ocorrência (e qualquer mês passado pendente, se a data
        // escolhida for retroativa).
        const diaVencimento = parseInt(formData.data.split('-')[2], 10);
        const res = await criarDespesaRecorrente({
          descricao: dados.descricao,
          valor: dados.valor,
          categoria: dados.categoria,
          diaVencimento,
          dataInicio: formData.data,
          duracaoMeses: formData.duracaoMeses ? parseInt(formData.duracaoMeses, 10) : null,
          observacoes: dados.observacoes,
        });
        despesaId = res.despesasGeradas?.[0]?.id;
        carregarRecorrencias();
      } else {
        const res = await criarDespesa(dados);
        despesaId = res.data?.data?.id || res.data?.id;
      }
      // Upload do comprovante selecionado (novo ou edição)
      if (arquivoComprovante && despesaId) {
        setUploadingComprovante(true);
        try {
          await uploadComprovante(despesaId, arquivoComprovante);
        } catch {
          setError('Despesa salva, mas erro ao anexar comprovante.');
        } finally {
          setUploadingComprovante(false);
        }
      }
      setSuccess(modoEdicao ? 'Despesa atualizada com sucesso!' : 'Despesa criada com sucesso!');
      fecharModal();
      carregarDados();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar despesa');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = (despesa) => {
    setItemExcluir(despesa);
    setOpenExcluirModal(true);
  };

  const confirmarExclusao = async () => {
    if (!itemExcluir) return;
    try {
      setLoading(true);
      await deletarDespesa(itemExcluir.id);
      setSuccess('Despesa excluída com sucesso!');
      setOpenExcluirModal(false);
      setItemExcluir(null);
      carregarDados();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir despesa');
    } finally {
      setLoading(false);
    }
  };

  // Filtros de mês/ano e pesquisa
  const meses = [
    { valor: 1, nome: 'Janeiro' }, { valor: 2, nome: 'Fevereiro' }, { valor: 3, nome: 'Março' },
    { valor: 4, nome: 'Abril' }, { valor: 5, nome: 'Maio' }, { valor: 6, nome: 'Junho' },
    { valor: 7, nome: 'Julho' }, { valor: 8, nome: 'Agosto' }, { valor: 9, nome: 'Setembro' },
    { valor: 10, nome: 'Outubro' }, { valor: 11, nome: 'Novembro' }, { valor: 12, nome: 'Dezembro' }
  ];
  const anos = [2023, 2024, 2025, 2026];

  const despesasFiltradas = despesas.filter(d => {
    const dataDespesa = new Date(d.data + 'T00:00:00');
    const passaMesAno = dataDespesa.getMonth() + 1 === mesFiltro && dataDespesa.getFullYear() === anoFiltro;
    if (pesquisa) {
      const termo = pesquisa.toLowerCase();
      const descMatch = d.descricao?.toLowerCase().includes(termo);
      const catMatch = d.categoria?.toLowerCase().includes(termo);
      return passaMesAno && (descMatch || catMatch);
    }
    return passaMesAno;
  });


  // Limite de despesas por mês no plano FREE
  const limiteDespesasFree = 15;
  const atingiuLimiteFree = planoAtual === 'FREE' && despesasFiltradas.length >= limiteDespesasFree;

  // Totais filtrados
  const totalDespesasFiltrado = despesasFiltradas.reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const totalFixasFiltrado = despesasFiltradas.filter(d => d.tipo === 'fixa').reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const totalVariaveisFiltrado = despesasFiltradas.filter(d => d.tipo === 'variavel').reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: 'calc(100vh - 100px)' }, gap: 2, backgroundColor: isDarkMode ? undefined : '#FFFAF5' }}>
      {/* PAINEL LATERAL ESQUERDO */}
      <Paper sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, borderTop: '3px solid #F97316', background: isDarkMode ? undefined : '#FFF7ED' }}>
        <Typography variant="h6" fontWeight={600} sx={{ borderLeft: '4px solid #F97316', pl: 1.5 }}>Despesas</Typography>
        {/* Filtro de Mês/Ano */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Mês</InputLabel>
            <Select
              value={mesFiltro}
              label="Mês"
              onChange={(e) => setMesFiltro(e.target.value)}
            >
              {meses.map(mes => (
                <MenuItem key={mes.valor} value={mes.valor}>{mes.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Ano</InputLabel>
            <Select
              value={anoFiltro}
              label="Ano"
              onChange={(e) => setAnoFiltro(e.target.value)}
            >
              {anos.map(ano => (
                <MenuItem key={ano} value={ano}>{ano}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ borderColor: 'rgba(249,115,22,0.2)' }} />
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.68em' }}>Total de Despesas</Typography>
            <Typography variant="h5" fontWeight={600} color="error.main">
              {formatarMoeda(totalDespesasFiltrado)}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(249,115,22,0.2)' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Fixas</Typography>
              <Typography variant="body2" fontWeight={500} color="primary.main">
                {formatarMoeda(totalFixasFiltrado)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Variáveis</Typography>
              <Typography variant="body2" fontWeight={600} color="warning.main">
                {formatarMoeda(totalVariaveisFiltrado)}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(249,115,22,0.2)' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">Lançamentos no período</Typography>
            <Typography variant="h6" fontWeight={500}>{despesasFiltradas.length}</Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={abrirModalNovo}
          fullWidth
          disabled={atingiuLimiteFree}
          sx={{
            mt: 'auto',
            bgcolor: '#F97316',
            fontWeight: 700,
            letterSpacing: '0.5px',
            boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
            '&:hover': { bgcolor: '#EA580C', boxShadow: '0 4px 14px rgba(249,115,22,0.45)' },
            '&:disabled': { bgcolor: 'rgba(249,115,22,0.3)' },
          }}
        >
          Nova Despesa
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={() => setOpenRecorrenciasModal(true)}
          sx={{ mt: 1, color: '#F97316', fontWeight: 600, textTransform: 'none' }}
        >
          Gerenciar despesas fixas {recorrencias.filter(r => r.ativa).length > 0 && `(${recorrencias.filter(r => r.ativa).length})`}
        </Button>
        {atingiuLimiteFree && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Limite de 15 despesas/mês atingido no plano grátis. Para lançar mais despesas, faça upgrade para o plano Pro.
          </Alert>
        )}
      </Paper>

      {/* ÁREA PRINCIPAL - LISTA */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        {/* Barra de Pesquisa */}
        <Paper sx={{ p: 2, mb: 2, borderTop: '2px solid rgba(249,115,22,0.3)' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Pesquisar por descrição ou categoria..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  <SearchIcon fontSize="small" />
                </Box>
              ),
            }}
          />
        </Paper>
        <Paper sx={{ flex: 1, overflow: 'auto', p: 0, borderTop: '2px solid rgba(249,115,22,0.25)' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : despesas.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={5}>
              <Typography color="text.secondary" gutterBottom>Nenhuma despesa cadastrada</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={abrirModalNovo} disabled={atingiuLimiteFree}>
                Adicionar primeira despesa
              </Button>
            </Box>
          ) : (
            <Box>
              {despesasFiltradas
                .sort((a, b) => new Date(b.data) - new Date(a.data))
                .map((despesa) => (
                  <Box
                    key={despesa.id}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: { xs: 1, sm: 0 },
                      p: 2,
                      borderBottom: '1px solid rgba(249,115,22,0.12)',
                      '&:hover': { bgcolor: 'rgba(249,115,22,0.04)' },
                    }}
                  >
                    {/* Data */}
                    <Box display="flex" alignItems="center" gap={2} flex={1} sx={{ minWidth: 0 }}>
                      <Box sx={{ minWidth: 50, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={500} color="text.secondary">
                          {new Date(despesa.data + 'T00:00:00').getDate().toString().padStart(2, '0')}/{new Date(despesa.data + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit' })}
                        </Typography>
                      </Box>
                      {/* Info */}
                      <Box flex={1} sx={{ minWidth: 0 }}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: { xs: 200, sm: 280 } }}>{despesa.descricao}</Typography>
                          <Chip
                            label={despesa.tipo}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20, fontSize: '0.7rem', fontWeight: 600,
                              borderColor: despesa.tipo === 'variavel' ? '#F97316' : '#3B82F6',
                              color: despesa.tipo === 'variavel' ? '#EA580C' : '#2563EB',
                            }}
                          />
                          {despesa.recorrenciaId && (
                            <Tooltip title="Gerada automaticamente por uma despesa fixa recorrente">
                              <Chip
                                label="Automática"
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'rgba(139,92,246,0.12)', color: '#7C3AED' }}
                              />
                            </Tooltip>
                          )}
                          {despesa.comprovanteUrl && (
                            <Tooltip title="Comprovante anexado">
                              <AttachFileIcon sx={{ fontSize: 14, color: 'success.main', cursor: 'pointer' }} onClick={() => window.open(despesa.comprovanteUrl, '_blank')} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {despesa.categoria}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Valor */}
                    <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 3 }} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
                      <Box textAlign="right" sx={{ minWidth: 100 }}>
                        <Typography variant="body1" fontWeight={600} color="error.main">
                          {formatarMoeda(despesa.valor)}
                        </Typography>
                      </Box>
                      {/* Ações */}
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirModalEdicao(despesa)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => handleDeletar(despesa)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      <Dialog open={openExcluirModal} onClose={() => setOpenExcluirModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
          <Typography variant="h6" fontWeight={600}>Confirmar exclusão</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Tem certeza que deseja excluir este lançamento?
          </Typography>
          {itemExcluir && (
            <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mt: 1 }}>
              <Typography variant="subtitle2">{itemExcluir.descricao}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatarData(itemExcluir.data)} • {itemExcluir.categoria} • {formatarMoeda(itemExcluir.valor)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setOpenExcluirModal(false)} size="small">
            Cancelar
          </Button>
          <Button
            onClick={confirmarExclusao}
            variant="contained"
            color="error"
            size="small"
            disabled={loading}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* MODAL CRIAR/EDITAR - MINIMALISTA COMPACTO */}
      <Dialog open={openModal} onClose={fecharModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
          <Typography variant="h6" fontWeight="600">
            {modoEdicao ? 'Editar despesa' : 'Nova despesa'}
          </Typography>
          <IconButton
            onClick={fecharModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2.5, pb: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Valor *"
                  fullWidth
                  size="small"
                  value={formData.valor}
                  onChange={(e) => {
                    const valorFormatado = formatarValorInput(e.target.value);
                    setFormData({ ...formData, valor: valorFormatado });
                  }}
                  error={formData.valor === '' || parseFloat(formData.valor.replace(/\./g, '').replace(/,/g, '.')) <= 0}
                  placeholder="0,00"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Data *"
                  fullWidth
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={categoriasDespesas}
                  getOptionLabel={(option) => option.nome || ''}
                  value={categoriasDespesas.find(cat => cat.nome === formData.categoria) || null}
                  freeSolo
                  onChange={(e, newValue) => {
                    if (typeof newValue === 'string') {
                      setFormData({ ...formData, categoria: newValue });
                    } else if (newValue) {
                      setFormData({ ...formData, categoria: newValue.nome });
                    }
                  }}
                  onInputChange={(e, newValue) => {
                    setFormData({ ...formData, categoria: newValue });
                  }}
                  inputValue={formData.categoria}
                  renderInput={(params) => (
                    <TextField {...params} label="Categoria *" required placeholder="Digite ou selecione" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2">{option.nome}</Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText={
                    <Box sx={{ py: 1, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Nenhuma categoria cadastrada</Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.tipo}
                    label="Tipo"
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  >
                    <MenuItem value="fixa">Fixa</MenuItem>
                    <MenuItem value="variavel">Variável</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Descrição *"
                  required
                  fullWidth
                  size="small"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                />
              </Grid>
              {!modoEdicao && formData.tipo === 'fixa' && (
                <Grid item xs={12}>
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.repetirMensalmente}
                          onChange={(e) => setFormData({ ...formData, repetirMensalmente: e.target.checked })}
                        />
                      }
                      label={<Typography variant="body2">Repetir automaticamente todo mês</Typography>}
                    />
                    {formData.repetirMensalmente && (
                      <TextField
                        label="Repetir por quantos meses"
                        type="number"
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                        value={formData.duracaoMeses}
                        onChange={(e) => setFormData({ ...formData, duracaoMeses: e.target.value })}
                        placeholder="Padrão: 12 meses"
                        helperText="Deixe em branco pra repetir por até 12 meses. As parcelas já aparecem todas de uma vez, inclusive as futuras."
                        inputProps={{ min: 1 }}
                      />
                    )}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Observações"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                {(comprovanteAtual || arquivoComprovante) ? (
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon fontSize="small" color="primary" />
                    <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {comprovanteAtual?.nome || arquivoComprovante?.name || 'Comprovante selecionado'}
                    </Typography>
                    {comprovanteAtual?.url && (
                      <Tooltip title="Visualizar">
                        <IconButton size="small" onClick={() => window.open(comprovanteAtual.url, '_blank')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remover">
                      <IconButton size="small" color="error" onClick={handleRemoverComprovante}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<AttachFileIcon />}
                  >
                    Anexar comprovante (opcional)
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => { if (e.target.files[0]) setArquivoComprovante(e.target.files[0]); }}
                    />
                  </Button>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={fecharModal} size="small">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={loading || !formData.valor || parseFloat(formData.valor) <= 0 || atingiuLimiteFree}
            >
              {loading ? 'Salvando...' : atingiuLimiteFree ? 'Limite do plano atingido' : 'Salvar'}
            </Button>
            {atingiuLimiteFree && (
              <Alert severity="warning" sx={{ ml: 2, my: 1, fontSize: 13 }}>
                Limite de 15 despesas/mês atingido no plano grátis.
              </Alert>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal de Despesas Fixas Recorrentes */}
      <Dialog open={openRecorrenciasModal} onClose={() => setOpenRecorrenciasModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
          <Typography variant="h6" fontWeight={600}>Despesas Fixas</Typography>
          <Typography variant="caption" color="text.secondary">
            Lançamentos que se repetem automaticamente todo mês
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {recorrencias.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Nenhuma despesa fixa cadastrada. Crie uma despesa com tipo "Fixa" e marque "Repetir automaticamente todo mês".
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {recorrencias.map((rec) => (
                <ListItem
                  key={rec.id}
                  sx={{ px: 0, py: 1.25, borderBottom: 1, borderColor: 'divider', opacity: rec.ativa ? 1 : 0.55 }}
                  secondaryAction={rec.ativa && (
                    <Button
                      size="small"
                      color="error"
                      disabled={cancelandoId === rec.id}
                      onClick={() => handleCancelarRecorrencia(rec.id)}
                    >
                      {cancelandoId === rec.id ? 'Cancelando...' : 'Cancelar'}
                    </Button>
                  )}
                >
                  <ListItemText
                    primary={`${rec.descricao} — ${formatarMoeda(rec.valor)}`}
                    secondary={
                      <>
                        {rec.categoria} • todo dia {rec.diaVencimento}
                        {` • ${rec.duracaoMeses || 12} meses`}
                        {!rec.ativa && ' • cancelada'}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setOpenRecorrenciasModal(false)} size="small">Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Despesas;
